import React, { useState, useEffect, useContext } from "react";
import { TextField, Dialog, DialogTitle, DialogContent, DialogActions, Select, MenuItem, Button, Typography, Grid, FormControl, InputLabel, Box } from "@mui/material";
import { ThemeContext } from "../../../../context/ThemeContext";
import { useTranslation } from "react-i18next";
import { useSymbolPrices } from "../../../../context/WebSocketContext";
import BidValue from "../../../global/BidAsk/BidValue";
import AskValue from "../../../global/BidAsk/AskValue";
import { useSelector } from "react-redux";
import { GoPlus } from "react-icons/go";
import { BiMinus } from "react-icons/bi";

const EditAlertDialog = ({ open, handleClose, alertData, handleSave, symbolData, handleUpdate, digit }) => {
    const [formData, setFormData] = useState({ ...alertData });
    const { darkMode } = useContext(ThemeContext)
    const { t } = useTranslation()
    // const symbols = useSymbolPrices();
    const { symbols } = useSelector(state => state.watchlist)


    useEffect(() => {
        if (alertData) {
            setFormData({ ...alertData });
        }

    }, [alertData]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
    };

    const handleSubmit = () => {
        handleUpdate(formData);
        handleClose();
    };

    const selectedSymbol = symbols[alertData?.symbolId];
    // console.log(selectedSymbol);
    const typeMap = {
        1: "alert_type_sound",
        2: "alert_type_sms",
    };

    const conditionMap = {
        1: "condition_bid_gt",
        2: "condition_bid_lt",
        3: "condition_ask_gt",
        4: "condition_ask_lt",
        5: "condition_balance_gt",
        6: "condition_balance_lt",
        7: "condition_equity_gt",
        8: "condition_equity_lt",
        9: "condition_marginlevel_gt",
        10: "condition_marginlevel_lt",
        11: "condition_pl_gt",
        12: "condition_pl_lt",
    };
   
    const StepInput = ({ value, onChange, digits }) => {
        const [inputValue, setInputValue] = useState(value.toString());
    
        useEffect(() => {
            if (parseFloat(inputValue) !== value) {
                setInputValue(value.toString());
            }
        }, [value]);
    
        const step = 1 / Math.pow(10, digits);
    
        const increment = () => {
            const newVal = parseFloat((value + step).toFixed(digits));
            onChange(newVal);
        };
    
        const decrement = () => {
            const newVal = parseFloat((value - step).toFixed(digits));
            onChange(newVal);
        };
    
        const handleBlur = () => {
            const parsed = parseFloat(inputValue);
            if (!isNaN(parsed)) {
                onChange(parsed);
            } else {
                setInputValue(value.toString()); // reset to last valid
            }
        };
    
        return (
            <Box display="flex" alignItems="center" justifyContent="space-between"
                border={`1px solid ${darkMode ? "var(--borderDark)" : 'var(--borderLight)'}`} borderRadius={1}>
                <Button variant="text" onClick={decrement} sx={{ minWidth: '30px', height: 40 }}><BiMinus /></Button>
                <TextField type="text" value={inputValue} onBlur={handleBlur} onChange={(e) => setInputValue(e.target.value)}
                    inputProps={{ inputMode: "decimal",
                        onKeyDown: (e) => {
                            if (e.key === 'e' || e.key === '-') {
                                e.preventDefault();
                            }
                        }
                    }} sx={{ input: { textAlign: 'center' }, width: 180, "& .MuiOutlinedInput-root": { height: 42, "& .MuiOutlinedInput-notchedOutline": { border: "none" }, "&:hover .MuiOutlinedInput-notchedOutline": { border: "none" }, "&.Mui-focused .MuiOutlinedInput-notchedOutline": { border: "none" }, }, }}
                />
                <Button variant="text" onClick={increment} sx={{ minWidth: '30px', height: 40 }}><GoPlus /></Button>
            </Box>
        );
    };
    
    return (
        <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
            <DialogTitle sx={{ color: "white", p: 1 }} className="bg-primary">
                <Typography id="symbol-details-modal" variant="body1" sx={{ color: darkMode ? "var(--white)" : "var(--white)" }} align="center" >{t('EditAlerts')}</Typography>
            </DialogTitle>
            <DialogContent sx={{ backgroundColor: darkMode ? "#151924" : "#f5f5f5", }}>
                <Box sx={{ mt: 2 }}>
                    {formData.symbolId &&
                        <Grid container spacing={2} >
                            <Grid item xs={6} alignItems="center" justifyContent="center" display='flex' flexDirection="column">
                                <Typography variant="body1" align="center" >{t('bid')}</Typography>
                                <BidValue symbolId={selectedSymbol?.id} digits={selectedSymbol.digits} />

                            </Grid>
                            <Grid item xs={6} alignItems="center" justifyContent="center" display='flex' flexDirection="column">
                                <Typography variant="body1" align="center">{t('ask')}</Typography>
                                <AskValue symbolId={selectedSymbol?.id} digits={selectedSymbol.digits} />

                            </Grid>
                        </Grid>
                    }
                    <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2, }}>

                        <FormControl fullWidth >
                            <InputLabel>{t('type')}</InputLabel>
                            <Select readOnly label={t('type')} name="type" value={formData.type || ""} onChange={handleChange} sx={{ height: 45 }}
                                MenuProps={{ PaperProps: { sx: { paddingY: 0, '& .MuiMenuItem-root': { minHeight: '30px', paddingY: '4px', }, }, }, }}>
                                {Object.entries(typeMap).map(([key, value]) => (
                                    <MenuItem sx={{ fontSize: 13.5 }} key={key} value={Number(key)}>{t(value)}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>

                        <FormControl fullWidth >
                            <InputLabel>{t('condition')}</InputLabel>
                            <Select label={t('condition')} name="condition" value={formData.condition || ""} onChange={handleChange} sx={{ height: 45 }}
                                MenuProps={{ PaperProps: { sx: { paddingY: 0, '& .MuiMenuItem-root': { minHeight: '30px', paddingY: '4px', }, }, }, }}>
                                {Object.entries(conditionMap).map(([key, value]) => (
                                    <MenuItem sx={{ fontSize: 13.5 }} key={key} value={Number(key)}>{t(value)}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        {formData.symbolId && <FormControl fullWidth sx={{ mt: "18px" }}>
                            <InputLabel>{t('SymbolList')}</InputLabel>
                            <Select label={t('SymbolList')} name="symbolId" value={formData.symbolId || ""} onChange={handleChange} sx={{ height: 45 }} disabled>
                                {Object.values(symbolData).map((symbol) => (
                                    <MenuItem key={symbol.id} value={symbol.id}>{symbol.name}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        }

                        <FormControl fullWidth>
                            <Typography variant="caption">{t('Value')}</Typography>
                            <StepInput value={formData.value || ""} digits={selectedSymbol?.digits} onChange={(val) => setFormData((prev) => ({ ...prev, value: val }))} />
                            {/* <TextField name="value" type="number" value={formData.value || ""} onChange={handleChange} variant="outlined" sx={{ height: 45 }} /> */}
                        </FormControl>

                    </Box>
                </Box>
            </DialogContent>

            <DialogActions sx={{ backgroundColor: darkMode ? "#151924" : "#f5f5f5", }}>
                <Button onClick={handleClose} variant="outlined" color="primary">{t('cancel')}</Button>
                <Button onClick={handleSubmit} variant="contained" color="primary">{t('save')}</Button>
            </DialogActions>
        </Dialog>
    );
};

export default EditAlertDialog;
